import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { get_north_star } from '../utils/services';

// Caché a nivel de módulo: al re-montar (p.ej. al colapsar el sidebar) mostramos el dato
// cacheado al instante en vez de parpadear a vacío mientras el fetch vuelve.
let northStarCache = null;
// Hasta cuándo (epoch ms) se reutiliza la respuesta sin volver a pedirla. El backend manda
// `expiresAt` = cuándo rota su propio snapshot, así la píldora nunca muestra una foto más vieja
// que el dashboard. Se acota porque se compara contra el reloj del navegador.
let northStarFreshUntil = 0;
let northStarInFlight = null;
const MIN_REUSE_MS = 30 * 1000; // también es la espera para reintentar tras un fallo
const MAX_REUSE_MS = 30 * 60 * 1000;
const FALLBACK_REUSE_MS = 60 * 1000; // backend sin `expiresAt`

function reuseWindowMs(data) {
  if (!data) return MIN_REUSE_MS;
  const expiresAt = Date.parse(data.expiresAt);
  const ms = Number.isFinite(expiresAt) ? expiresAt - Date.now() : FALLBACK_REUSE_MS;
  return Math.min(MAX_REUSE_MS, Math.max(MIN_REUSE_MS, ms));
}

// Una sola petición para todos los montajes, y ninguna mientras la respuesta siga vigente.
function loadNorthStar(token) {
  if (Date.now() < northStarFreshUntil) return Promise.resolve(northStarCache);
  if (!northStarInFlight) {
    northStarInFlight = get_north_star(token)
      .then((d) => {
        if (d) northStarCache = d;
        northStarFreshUntil = Date.now() + reuseWindowMs(d);
        return northStarCache;
      })
      .finally(() => {
        northStarInFlight = null;
      });
  }
  return northStarInFlight;
}

const MONTHS_ES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
function monthLabel(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${MONTHS_ES[parseInt(m, 10) - 1] || m} ${y}`;
}

// Delta en % si el mes previo > 0 (p.ej. +66%); si viene de 0, absoluto (+3).
function deltaText(cur, prev) {
  const d = cur - prev;
  if (prev > 0) {
    const pct = Math.round((d / prev) * 100);
    return `${pct >= 0 ? '+' : ''}${pct}%`;
  }
  return `${d >= 0 ? '+' : ''}${d}`;
}
const arrow = (d) => (d > 0 ? '▲' : d < 0 ? '▼' : '—');
const pluralize = (n, singular, plural) => (n === 1 ? singular : plural);
const deltaColor = (d) => (d > 0 ? '#16a34a' : d < 0 ? '#dc2626' : '#9ca3af'); // sobre blanco
const deltaColorDark = (d) => (d > 0 ? '#4ade80' : d < 0 ? '#f87171' : '#9ca3af'); // sobre la barra oscura

function Sparkline({ data, color, width = 116, height = 28 }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const range = max - min || 1;
  const stepX = width / (data.length - 1 || 1);
  const xy = (v, i) => [i * stepX, height - ((v - min) / range) * (height - 5) - 3];
  const pts = data.map((v, i) => xy(v, i).map((n) => n.toFixed(1)).join(',')).join(' ');
  const [lx, ly] = xy(data[data.length - 1], data.length - 1);
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={lx} cy={ly} r="2.5" fill={color} />
    </svg>
  );
}

// Una meta dentro del recuadro gris: etiqueta, número + unidad + delta, y sparkline debajo.
// La unidad (p.ej. "TOMi8", "maestros") aclara QUÉ cuenta el número — sin ella, "101" y "3"
// quedaban ambiguos (feedback de Juan). Ojo: cada meta cuenta una unidad DISTINTA (Meta 1 =
// TOMi8, Meta 2 = maestros), por eso la unidad se pasa por columna.
function MetricColumn({ label, metric, color, unit }) {
  return (
    <div style={{ flex: 1, padding: '2px 14px', minWidth: 0 }}>
      <div style={{ fontSize: 11.5, color: '#6b7280', marginBottom: 6, whiteSpace: 'nowrap' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 24, fontWeight: 700, color: '#111827', lineHeight: 1 }}>{metric.current}</span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: deltaColor(metric.delta) }}>
          {arrow(metric.delta)} {deltaText(metric.current, metric.previous)}
        </span>
      </div>
      {unit ? (
        <div style={{ fontSize: 12, fontWeight: 600, color: '#6b7280', marginTop: 3 }}>{unit}</div>
      ) : null}
      <div style={{ marginTop: 8 }}>
        <Sparkline data={metric.series} color={color} />
      </div>
    </div>
  );
}

export default function NorthStar({ token }) {
  const [data, setData] = useState(northStarCache);
  const [open, setOpen] = useState(false);
  // Mes mostrado: 'closed' = último mes cerrado (default, KR estable) · 'current' = mes en
  // curso (parcial). El toggle ajusta números, deltas y sparklines a la vez.
  const [mode, setMode] = useState('closed');
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    loadNorthStar(token).then((d) => {
      if (!cancelled && d) setData(d);
    });
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Backend backward-compatible: las anclas del toggle viven en `data.anchors` (aparte de
  // `data.meta1`/`meta2` planos que usa el admin viejo). Si aún no hay data (fetch en vuelo) o
  // el backend es el viejo (sin `anchors`), ocultamos el widget en vez de crashear.
  const anchors = data && data.anchors;
  if (!anchors || !anchors.meta1 || !anchors.meta1.closed || !anchors.meta2 || !anchors.meta2.closed)
    return null;
  const { projectId } = data;
  // Ancla seleccionada por el toggle. Cada meta expone { closed, current }.
  const meta1 = anchors.meta1[mode];
  const meta2 = anchors.meta2[mode];
  const month = mode === 'closed' ? data.monthClosed : data.monthCurrent;
  const monthCaption = mode === 'closed' ? 'último mes cerrado' : 'mes en curso';

  const goToGlobal = () => {
    setOpen(false);
    // Abrir el lab en el MISMO mes que muestra el widget: closed = mes anterior (offset 1),
    // current = mes en curso (offset 0). El lab anima el toggle si abre en el anterior.
    const nsMonthOffset = mode === 'closed' ? 1 : 0;
    // `d_` = parámetro para el dashboard embebido (ver dashParams en Project_admin).
    if (projectId != null) navigate(`/project?id=${projectId}&d_nsMonthOffset=${nsMonthOffset}`);
  };

  const pillStat = (tag, metric, title) => (
    <span title={title} style={{ whiteSpace: 'nowrap' }}>
      <span style={{ color: 'rgba(255,255,255,0.55)' }}>{tag} </span>
      <b style={{ color: '#fff' }}>{metric.current}</b>{' '}
      <span style={{ color: deltaColorDark(metric.delta), fontWeight: 600 }}>
        {arrow(metric.delta)}
        {deltaText(metric.current, metric.previous)}
      </span>
    </span>
  );

  return (
    <div className="app-header__northstar" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="North Star de empresa — clic para el detalle"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '5px 12px',
          borderRadius: 999,
          border: '1px solid rgba(255,255,255,0.12)',
          background: open ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.05)',
          color: '#fff',
          fontSize: 13,
          cursor: 'pointer',
          lineHeight: 1.2,
        }}
      >
        {pillStat('M1', meta1, 'Meta 1 · TOMis con uso ≥8h')}
        <span style={{ color: 'rgba(255,255,255,0.3)' }}>·</span>
        {pillStat('M2', meta2, 'Meta 2 · Maestros con recuperación lograda')}
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10 }}>▾</span>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#fff',
            borderRadius: 14,
            boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
            border: '1px solid #e5e7eb',
            zIndex: 1000,
            padding: 12,
            cursor: 'default',
          }}
        >
          {/* Toggle mes en curso / cerrado — ajusta números, deltas y sparklines. */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <div style={{ display: 'inline-flex', background: '#eef0f3', borderRadius: 999, padding: 2 }}>
              {[
                { key: 'current', label: 'Mes actual' },
                { key: 'closed', label: 'Mes anterior' },
              ].map((opt) => {
                const active = mode === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setMode(opt.key)}
                    aria-pressed={active}
                    style={{
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: 999,
                      padding: '4px 12px',
                      fontSize: 11.5,
                      fontWeight: 600,
                      background: active ? '#fff' : 'transparent',
                      color: active ? '#5800ff' : '#6b7280',
                      boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                      transition: 'color 0.15s, background 0.15s',
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'stretch',
              background: '#f4f5f7',
              borderRadius: 12,
              padding: '12px 4px',
            }}
          >
            <MetricColumn label="Meta 1 · Uso ≥8h" metric={meta1} color="#5800ff" unit="TOMi8" />
            <div style={{ width: 1, background: '#e2e4e9', margin: '2px 0' }} />
            <MetricColumn
              label="Meta 2 · Recuperación"
              metric={meta2}
              color="#16a34a"
              unit={pluralize(meta2.current, 'maestro', 'maestros')}
            />
          </div>
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              fontSize: 11,
            }}
          >
            <span style={{ color: '#9ca3af' }}>{monthLabel(month)} · {monthCaption}</span>
            {projectId != null && (
              <button
                type="button"
                onClick={goToGlobal}
                title="Abrir el proyecto TOMi Global"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  color: '#9299a3',
                  fontSize: 11,
                  fontWeight: 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#5800ff')}
                onMouseLeave={(e) => (e.currentTarget.style.color = '#9299a3')}
              >
                TOMi Global ↗
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
